import {Link, useParams} from "react-router-dom";
import {
  Button,
  Card, Collapse,
  Divider, Flex,
  Image,
  List,
  Popover,
  Skeleton,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import React, {useContext, useEffect, useMemo, useState, useCallback} from "react";
import {getGenerativeSessionHistory} from "../../../../../services/generative-message-logs";
import {formatDate} from "../../../../../utils";
import {
  ArrowLeftOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  DollarOutlined,
  ExperimentOutlined,
  CopyOutlined, MessageOutlined,
} from "@ant-design/icons";
import {getSourceData} from "../../../../../utils/extensionIconFormatter";
import UserContext from "../../../../../context/users";
import {AdminRole} from "../../../../../constants";
import {useGenerativeSessionSeed} from "../../../../../context/GenerativeSessionContext";
import {Info} from "lucide-react";
import { toast } from 'sonner'

function CopyButton({textToCopy}) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    });
  }, [textToCopy]);

  if (!textToCopy) {
    return null;
  }

  return (
    <Tooltip title={isCopied ? 'Copiado!' : 'Copiar'}>
      <Button
        type="text"
        size="small"
        icon={<CopyOutlined/>}
        onClick={handleCopy}
      />
    </Tooltip>
  );
}

export default function View() {
  const {historic_id} = useParams();
  const [data, setData] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(6);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user] = useContext(UserContext);
  const {setGenerativeSessionSeedId, loadingSeedId} = useGenerativeSessionSeed()
  const {
    token: {colorPrimary},
  } = theme.useToken();

  useEffect(() => {
    setLoading(true);
    getGenerativeSessionHistory({
      sessionID: historic_id,
      perPage,
      page,
    })
      .then(({data, total}) => {
        setData(data);
        setTotal(total);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Falha ao carregar histórico.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [perPage, page, historic_id]);

  if (loading) return <Skeleton active/>;

  return (
    <List
      loading={loading}
      pagination={{
        position: "bottom", align: "end", onChange: (page, perPage) => {
          setPage(page);
          setPerPage(perPage);
        }, total, pageSize: perPage, current: page,
      }}>
      <Space direction={"vertical"} style={{width: "100%"}}>
        <Link to={".."}>
          <Button icon={<ArrowLeftOutlined/>} style={{padding: 2}} type={"text"}>
            Histórico
          </Button>
        </Link>
        <div style={{display: 'flex', justifyContent: 'space-between'}}>
          <Typography.Title level={4}>
            {historic_id}
            {user.role === AdminRole && data.length > 0 && <Tooltip
              title={
                <>
                  <b>Provedores:</b> {Array.from(new Set(data.map(item => item.llmProvider))).join(",")} <br/>
                  <b>Modelos:</b> {Array.from(new Set(data.map(item => item.llmModel))).join(",")} <br/>
                  <b>Input
                    tokens:</b> {data.reduce(
                  (acc, item) =>
                    acc +
                    Number(item.costs.integration.inputTokens) +
                    Number(item.costs.message.inputTokens) +
                    Number(item.costs.reranking.inputTokens),
                  0
                )}
                  <br/>
                  <b>Output
                    tokens:</b> {data.reduce(
                  (acc, item) =>
                    acc +
                    Number(item.costs.integration.outputTokens) +
                    Number(item.costs.message.outputTokens) +
                    Number(item.costs.reranking.outputTokens),
                  0
                )}
                  <br/>
                  <b>Custos com LLM:</b> $ {data.reduce(
                  (acc, item) =>
                    acc +
                    Number(item.costs.total.llmCost),
                  0
                )} <br/>
                  <b>Custo Total:</b> $ {data.reduce(
                  (acc, item) =>
                    acc +
                    Number(item.costs.total.cost),
                  0
                )} <br/>
                  <b>Custo Reranking:</b> $ {data.reduce(
                  (acc, item) =>
                    acc +
                    Number(item.costs.reranking.cost),
                  0
                )} <br/>
                  <b>Custo Gatilhos:</b> $ {data.reduce(
                  (acc, item) =>
                    acc +
                    Number(item.costs.integration.cost),
                  0
                )}
                </>}>
              <DollarOutlined style={{color: colorPrimary, paddingLeft: "10px"}}/>
            </Tooltip>}
          </Typography.Title>
          <Tooltip title="Usar sessão no teste">
            <Button
              loading={loadingSeedId === historic_id}
              onClick={() => setGenerativeSessionSeedId(historic_id)} icon={<ExperimentOutlined/>}/>
          </Tooltip>
        </div>

        <Typography.Text>
          {data.length > 0 && new Date(data[0]?.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </Typography.Text>
        <Space
          direction={"vertical"}
          style={{
            maxHeight: "600px",
            overflowY: "scroll",
            width: "100%",
          }}
        >
          {data.map((item, i) => (
            <Message key={i} {...item} />
          ))}
        </Space>
      </Space>
    </List>
  );
}

function Message({
                   userMessage,
                   response,
                   isAnythingElse,
                   createdAt,
                   responseTime,
                   clientData,
                   imageContents,
                   failed,
                   failure,
                 }) {
  const {
    token: {colorPrimary, colorTextTertiary, colorBgLayout},
  } = theme.useToken();

  const responseTextToCopy = useMemo(() => {
    if (!response) return "";
    return response
      .filter(item => item.type === "text" && item.data)
      .map(item => item.data)
      .join("\n\n");
  }, [response]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        gap: "16px",
      }}
    >
      <Divider>
        <Tooltip title={`Tempo de resposta: ${(responseTime / 1000).toFixed(2)}s`}>
          <ClockCircleOutlined
            style={{color: colorPrimary, paddingRight: "10px"}}
          />
        </Tooltip>
        {formatDate(new Date(createdAt))}
      </Divider>
      <Space>
        {clientData && Object.keys(clientData).map((item) => <Tag
          key={item}>{item}: {clientData[item]}</Tag>)}
      </Space>
      <div style={{display: 'flex', alignItems: 'center', gap: '8px', width: "fit-content", maxWidth: "80%"}}>
        <Card size={"small"}>
          <Typography.Text style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
            <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
              {imageContents?.map((img, index) => <Image key={index} width={250} src={img.url}/>)}
              {userMessage}
            </div>
          </Typography.Text>
        </Card>
        <CopyButton textToCopy={userMessage}/>
      </div>

      {failed && (
        <div
          style={{
            width: "fit-content",
            alignSelf: "flex-end",
            maxWidth: "80%",
          }}
        >
          <Card size="small" style={{borderColor: "#faad14", background: colorBgLayout}}>
            <Space direction="vertical" size={4}>
              <Tag icon={<WarningOutlined/>} color="warning">
                Falha no processamento
              </Tag>
              <Typography.Text>
                {failure?.message || "Não foi possível gerar uma resposta para esta mensagem."}
              </Typography.Text>
              <Typography.Text type="secondary" style={{fontSize: 12}}>
                {[failure?.stage, failure?.httpStatus && `HTTP ${failure.httpStatus}`]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography.Text>
            </Space>
          </Card>
        </div>
      )}

      {response?.filter(item => ['think'].includes(item.type))?.map((item, i) => (
        <div
          style={{
            width: "fit-content",
            alignSelf: "flex-end",
            maxWidth: "80%",
          }}
          key={i}
        >
          <Collapse
            bordered={false}
            expandIconPosition="end"
            style={{background: 'transparent', width: '100%'}}
          >
            <Collapse.Panel
              key="1"
              style={{
                background: colorBgLayout,
                borderRadius: '10px',
                border: 'none',
                opacity: 0.8
              }}
              header={
                <Space>
                  <MessageOutlined style={{color: colorTextTertiary}}/>
                  <Typography.Text style={{color: colorTextTertiary}}>
                    Pensamento
                  </Typography.Text>
                </Space>
              }
            >
              <Typography.Text style={{color: colorTextTertiary}} italic>
                {item.data}
              </Typography.Text>
            </Collapse.Panel>
          </Collapse>
        </div>
      ))}

      {response?.filter(item => ['text', 'trigger', 'reference', 'image', 'imageV2'].includes(item.type))?.map((item, i) => (
        <div
          style={{
            width: "fit-content",
            alignSelf: "flex-end",
            maxWidth: "80%",
          }}
          key={i}
        >
          <Flex align={'center'} gap={12}>
            {item.type === 'trigger' && <Tooltip title={'Essa interação conta como uma mensagem individual. \nInteração de número '+ (i+1)}><Info className={'w-4 opacity-50'} color={colorTextTertiary}/></Tooltip>}
            <Card size={"small"} style={{background: colorBgLayout, padding: 0}}>
              {item.type === "text" && <TextMessage text={item.data}/>}
              {item.type === "trigger" && <TriggerMessage data={item.data}/>}
              {item.type === "reference" && <ReferenceMessage data={item.data}/>}
              {item.type === "image" && <ImageMessage data={item.data}/>}
              {item.type === "imageV2" && <ImageMessage data={item.data}/>}
            </Card>
          </Flex>
        </div>
      ))}

      <div
        style={{
          width: "fit-content",
          alignSelf: "flex-end",
          maxWidth: "80%",
        }}
      >
        <CopyButton textToCopy={responseTextToCopy}/>
      </div>

      <div
        style={{
          width: "fit-content",
          alignSelf: "flex-end",
          maxWidth: "80%",
        }}
      >
        {isAnythingElse && (
          <Tag icon={<WarningOutlined/>} color={"warning"}>
            Fora do contexto
          </Tag>
        )}
      </div>
    </div>
  );
}

function ImageMessage({data}) {
  const parsedData = useMemo(() => {
    try {
      return JSON.parse(data ?? "{}");
    } catch {
      return {}
    }
  }, [data]);

  if (!parsedData?.url) {
    return null;
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <Image
        width={250}
        src={parsedData.url}
        alt={parsedData.caption || "Imagem enviada"}
      />
      {parsedData.caption && (
        <Typography.Text
          style={{whiteSpace: 'pre-line'}}
        >
          {parsedData.caption}
        </Typography.Text>
      )}
    </div>
  );
}

function TextMessage({text}) {
  const {Paragraph} = Typography;

  return (
    <Paragraph style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0}}>
      {text}
    </Paragraph>
  );
}

function ThinkMessage({text}) {
  const {Paragraph} = Typography;

  return (
    <Paragraph style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0}}>
      {text}
    </Paragraph>
  );
}

function TriggerMessage({data}) {
  const parsedData = useMemo(() => {
    try {
      return JSON.parse(data ?? "{}")
    } catch {
      return {}
    }
  }, [data]);

  const functionPretty = useMemo(
    () =>
      `${parsedData?.function_name}(${Object.values(parsedData?.args ?? {})?.join(
        ", "
      )})`,
    [parsedData]
  );

  return (
    <Popover content={<code>{JSON.stringify(parsedData, null, "\t")}</code>}>
      <Tag icon={<ThunderboltOutlined/>} color={"orange"}>
        {functionPretty}
      </Tag>
    </Popover>
  );
}

function ReferenceMessage({data}) {
  const parsedData = useMemo(() => {
    try {
      return JSON.parse(data ?? "[]")
    } catch {
      return []
    }
  }, [data]);
  return (
    <List
      dataSource={parsedData}
      grid={{gutter: 5}}
      renderItem={item => {
        const {text, icon, link} = getSourceData(item)
        const linkIcon = link ? <><Link target="_blank" rel="noopener noreferrer"
                                        to={link}><LinkOutlined/></Link></> : '';
        return (
          <List.Item style={{margin: 0, padding: 0}}>
            <Space direction={"vertical"}>
              <Popover
                title={<p>{item?.title} {linkIcon}</p>}
                content={
                  <Typography.Text>
                    {text} <br/>
                    Score: {item?.score?.toFixed(2)}<br/>
                  </Typography.Text>
                }>
                <Button>{item && icon}</Button>
              </Popover>
            </Space>
          </List.Item>
        )
      }}
    />
  );
}
